import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    CaseResult,
    RecordTypeResult,
    CarbonCopyContactResult,
    InsertableArchiveResult,
    EmailDocumentCategoryResult,
    ValidateArchiveAndCaseResult
} from '../../../services/api/dataServiceOutlookQuickSave';
import { IDocumentDetails } from '../../../@types/models';
import { submitQuickSave } from '../utils/submitHelpers';

export interface UseQuickSaveSubmitProps {
    title: string;
    selectedArchive: InsertableArchiveResult;
    selectedCategory: EmailDocumentCategoryResult;
    selectedCase?: CaseResult | null;
    selectedRecordType?: RecordTypeResult | null;
    selectedCopyTo: CarbonCopyContactResult[];
    caseValidation: ValidateArchiveAndCaseResult | null;
    showRecordTypes?: boolean; // Add this to determine if record type is required
    onSaveSuccess: (documentDetails?: IDocumentDetails | null) => void;
    onSaveError: (error: string) => void;
    onImportArchive: (documentDetails: IDocumentDetails) => void;
}

export function useQuickSaveSubmit({
    title,
    selectedArchive,
    selectedCategory,
    selectedCase,
    selectedRecordType,
    selectedCopyTo,
    caseValidation,
    showRecordTypes = false,
    onSaveSuccess,
    onSaveError,
    onImportArchive,
}: UseQuickSaveSubmitProps) {
    const { t } = useTranslation();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = (): void => {
        // Record type is only required when showRecordTypes is true
        const isRecordTypeRequired = showRecordTypes;

        if (!selectedCase || (isRecordTypeRequired && !selectedRecordType)) {
            onSaveError(t('Missing required fields'));
            return;
        }

        const callbacks = {
            onStart: () => setIsSubmitting(true),
            onSuccess: (documentDetails?: IDocumentDetails | null) => {
                setIsSubmitting(false);
                onSaveSuccess(documentDetails);
            },
            onError: (error: string) => {
                setIsSubmitting(false);
                onSaveError(error);
            },
            onImportArchive: (documentDetails: IDocumentDetails) => {
                setIsSubmitting(false);
                onImportArchive(documentDetails);
            }
        };

        const data = {
            title,
            selectedArchive,
            selectedCategory,
            selectedCase,
            selectedRecordType: selectedRecordType ?? null, // Convert undefined to null for SubmitData interface
            selectedCopyTo,
            caseValidation,
        };

        submitQuickSave(data, callbacks);
    };

    return {
        isSubmitting,
        handleSubmit,
    };
}
